import { useState, useEffect, useRef } from "react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Card } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Checkbox } from "./ui/checkbox";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "./ui/accordion";
import { Loader2, Save, Flag, ClipboardCheck, AlertTriangle, Sparkles, Globe, Upload, Trash2, Image as ImageIcon } from "lucide-react";

const PHOTO_SLOTS = [
  { key: "tetto", label: "Foto tetto" },
  { key: "quadro_elettrico", label: "Foto quadro elettrico" },
  { key: "accesso", label: "Foto accesso alla struttura" },
  { key: "area_inverter", label: "Foto area inverter" },
];

function PhotoSlot({ leadId, slot, label, photo, onChanged }) {
  const [blobUrl, setBlobUrl] = useState(null);
  const [busy, setBusy] = useState(false);
  const inputRef = useRef(null);
  useEffect(() => {
    let url;
    if (photo?.storage_path) {
      api.get(`/files/${photo.storage_path}`, { responseType: "blob" })
        .then((r) => { url = URL.createObjectURL(r.data); setBlobUrl(url); })
        .catch(() => setBlobUrl(null));
    } else setBlobUrl(null);
    return () => { if (url) URL.revokeObjectURL(url); };
  }, [photo?.storage_path]);
  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post(`/leads/${leadId}/pre-fattibilita/photo?slot=${slot}`, fd);
      onChanged?.(data);
      toast.success("Foto caricata");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Errore nel caricamento"); }
    finally { setBusy(false); if (inputRef.current) inputRef.current.value = ""; }
  };
  const remove = async () => {
    setBusy(true);
    try {
      const { data } = await api.delete(`/leads/${leadId}/pre-fattibilita/photo/${slot}`);
      onChanged?.(data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <div className="space-y-1.5" data-testid={`pf-photo-${slot}`}>
      <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold">{label}</div>
      <div className="relative border border-border rounded overflow-hidden bg-secondary/30 h-32">
        {blobUrl ? (
          <img src={blobUrl} alt={label} className="h-32 w-full object-cover" />
        ) : (
          <button type="button" onClick={() => inputRef.current?.click()} disabled={busy} className="h-32 w-full flex flex-col items-center justify-center gap-1 text-muted-foreground hover:text-primary transition-colors" data-testid={`pf-photo-upload-${slot}`}>
            {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : <><Upload className="h-5 w-5" /><span className="text-xs">Carica foto</span></>}
          </button>
        )}
        {blobUrl && (
          <button type="button" onClick={remove} disabled={busy} className="absolute top-1 right-1 rounded bg-black/60 text-white p-1 hover:bg-destructive transition-colors" data-testid={`pf-photo-delete-${slot}`}>
            {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />}
          </button>
        )}
      </div>
      <input ref={inputRef} type="file" accept="image/*" className="hidden" onChange={(e) => upload(e.target.files?.[0])} data-testid={`pf-photo-input-${slot}`} />
    </div>
  );
}

const IV_FIELDS = [
  { key: "tipo_immobile", label: "Tipo di immobile" },
  { key: "installazione", label: "Luogo di installazione" },
  { key: "impianto_esistente", label: "Impianto esistente" },
  { key: "situazioni", label: "Situazioni particolari" },
  { key: "tempistica", label: "Tempistica progetto" },
];

function IntervistaWebCard({ data }) {
  const val = (k) => { const v = data[k]; return Array.isArray(v) ? (v.length ? v.join(", ") : "—") : (v || "—"); };
  return (
    <Card className="rounded border-primary/30 bg-primary/5" data-testid="intervista-web-card">
      <div className="p-5">
        <h2 className="font-heading font-semibold flex items-center gap-2 mb-3"><Globe className="h-5 w-5 text-primary" strokeWidth={1.5} /> Intervista automatica dal web</h2>
        <div className="grid sm:grid-cols-2 gap-x-6 gap-y-3">
          {IV_FIELDS.map((f) => (
            <div key={f.key} data-testid={`ivw-${f.key}`}>
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold">{f.label}</div>
              <div className="text-sm">{val(f.key)}</div>
            </div>
          ))}
        </div>
        {data.updated_at && <p className="text-[10px] text-muted-foreground mt-3">Compilata dal cliente sul configuratore pubblico</p>}
      </div>
    </Card>
  );
}

const SECTIONS = [
  { key: "copertura", title: "Copertura", groups: [
    { key: "tipologia", label: "Tipologia", options: ["Falda", "Piano"] },
    { key: "materiale", label: "Materiale", options: ["Lamiera grecata", "Sandwich", "Coppo", "Tegola", "Guaina", "Fibrocemento"], altro: true },
    { key: "stato", label: "Stato di conservazione", options: ["Ottimo", "Buono", "Da verificare", "Degrado evidente", "Infiltrazioni", "Necessità rifacimento"] },
    { key: "eta", label: "Età copertura", options: ["<10 anni", "10-20 anni", ">20 anni", "Sconosciuta"] },
  ]},
  { key: "superficie", title: "Superficie", groups: [
    { key: "superficie", options: ["Superficie sufficiente", "Superficie limitata", "Presenza ostacoli", "Abbaini", "Camini", "Lucernari", "Antenne", "Linee vita", "Impianti esistenti", "Altri impedimenti"] },
  ]},
  { key: "orientamento", title: "Orientamento", groups: [{ key: "orientamento", options: ["Sud", "Sud-Est", "Sud-Ovest", "Est", "Ovest", "Nord"] }] },
  { key: "inclinazione", title: "Inclinazione", groups: [{ key: "inclinazione", options: ["Conforme", "Da verificare"] }] },
  { key: "ombreggiamenti", title: "Ombreggiamenti", groups: [{ key: "ombreggiamenti", options: ["Nessuno", "Alberi", "Fabbricati", "Comignoli", "Parapetti"], altro: true }] },
  { key: "struttura", title: "Struttura", groups: [{ key: "struttura", options: ["Struttura in legno", "Cemento", "Laterocemento", "Acciaio", "Necessità verifica strutturale", "Carichi da verificare"], altro: true }] },
  { key: "accessibilita", title: "Accessibilità", groups: [{ key: "accessibilita", options: ["Accesso semplice", "Necessaria PLE", "Necessario ponteggio", "Necessaria autogru", "Accesso limitato", "Area scarico disponibile"] }] },
  { key: "elettrico", title: "Impianto elettrico", groups: [
    { key: "contatore", label: "Contatore", options: ["Monofase", "Trifase", "Cabina MT"] },
    { key: "quadro", label: "Quadro elettrico", options: ["Accessibile", "Da adeguare", "Da sostituire"] },
    { key: "percorso", label: "Percorso cavi", options: ["Disponibile", "Da realizzare", "Complesso"] },
    { key: "distanza", label: "Distanza tetto-quadro", options: ["<15 m", "15-30 m", ">30 m"] },
  ]},
  { key: "spazi", title: "Spazi tecnici", groups: [
    { key: "inverter", label: "Inverter", options: ["Posizione disponibile", "Da individuare"] },
    { key: "batteria", label: "Batteria", options: ["Locale disponibile", "Ventilazione idonea", "Parete idonea"] },
  ]},
  { key: "vincoli", title: "Vincoli", groups: [{ key: "vincoli", options: ["Centro storico", "Paesaggistico", "Condominio", "Soprintendenza"], altro: true }] },
  { key: "pratiche", title: "Pratiche", groups: [{ key: "pratiche", options: ["POD verificato", "Potenza impegnata verificata", "Cabina verificata", "Coordinate rilevate"] }] },
  { key: "documentazione", title: "Documentazione raccolta", groups: [{ key: "documentazione", options: ["Bolletta", "Foto tetto", "Foto quadro", "Foto contatore", "Foto percorso cavi", "Planimetria", "Coordinate"] }] },
  { key: "esito", title: "Esito tecnico", groups: [
    { key: "installabilita", label: "Installabilità", options: ["Installabile senza criticità", "Installabile con verifiche", "Installabile con opere preliminari", "Non installabile"] },
    { key: "verifiche", label: "Verifiche necessarie", options: ["Strutturale", "Elettrica", "Autorizzativa", "Architettonica", "Geologica", "Connessione"], altro: true },
  ]},
];

const INSTALL_TONE = {
  "Installabile senza criticità": "bg-emerald-500/15 text-emerald-600 border-emerald-500/30",
  "Installabile con verifiche": "bg-amber-500/15 text-amber-600 border-amber-500/30",
  "Installabile con opere preliminari": "bg-orange-500/15 text-orange-600 border-orange-500/30",
  "Non installabile": "bg-red-500/15 text-red-600 border-red-500/30",
};

const slug = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");

// Genera automaticamente le considerazioni tecniche dai dati selezionati.
function buildConsiderations(sel, altro, blockLabels) {
  const recap = SECTIONS.map((sec) => {
    const items = sec.groups.flatMap((g) => {
      const path = `${sec.key}.${g.key}`;
      return (sel[path] || []).map((o) => (o === "Altro" && altro[path] ? `Altro: ${altro[path]}` : o));
    });
    return items.length ? { title: sec.title, text: items.join(", ") } : null;
  }).filter(Boolean);
  return recap;
}

export default function PreFattibilita({ lead, onUpdate, selfToken }) {
  const pf = lead.pre_fattibilita || {};
  const [sel, setSel] = useState(pf.selections || {});
  const [altro, setAltro] = useState(pf.altro || {});
  const [bloccanti, setBloccanti] = useState(new Set(pf.bloccanti || []));
  const [saving, setSaving] = useState(false);

  const isSel = (path, opt) => (sel[path] || []).includes(opt);
  const toggleOpt = (path, opt) => setSel((s) => {
    const cur = new Set(s[path] || []);
    cur.has(opt) ? cur.delete(opt) : cur.add(opt);
    return { ...s, [path]: [...cur] };
  });
  const isBlock = (path, opt) => bloccanti.has(`${path}::${opt}`);
  const toggleBlock = (path, opt) => setBloccanti((b) => {
    const nb = new Set(b); const k = `${path}::${opt}`;
    nb.has(k) ? nb.delete(k) : nb.add(k); return nb;
  });

  const activeBlock = [...bloccanti].filter((k) => { const [p, o] = k.split("::"); return (sel[p] || []).includes(o); });
  const blockLabels = activeBlock.map((k) => k.split("::")[1]);
  const esito = (sel["esito.installabilita"] || [])[0];
  const recap = buildConsiderations(sel, altro, blockLabels);
  const autoText = [
    ...recap.map((r) => `${r.title}: ${r.text}`),
    blockLabels.length ? `Condizioni bloccanti: ${blockLabels.join(", ")}` : null,
  ].filter(Boolean).join("\n");

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put(selfToken ? `/public/self/${selfToken}/pre-fattibilita` : `/leads/${lead.id}/pre-fattibilita`, { selections: sel, altro, bloccanti: activeBlock, sintesi: autoText });
      if (!selfToken) onUpdate?.(data); else onUpdate?.("saved");
      toast.success("Pre-fattibilità salvata");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const OptionRow = ({ path, opt }) => {
    const checked = isSel(path, opt);
    return (
      <div className="flex items-center gap-2 py-1">
        <Checkbox id={`pf-${path}-${slug(opt)}`} checked={checked} onCheckedChange={() => toggleOpt(path, opt)} data-testid={`pf-${path}-${slug(opt)}`} />
        <label htmlFor={`pf-${path}-${slug(opt)}`} className="text-sm flex-1 cursor-pointer leading-tight">{opt}</label>
        {checked && (
          <button type="button" onClick={() => toggleBlock(path, opt)} title="Segna come condizione bloccante" data-testid={`pf-block-${path}-${slug(opt)}`}
            className={`shrink-0 rounded p-1 transition-colors ${isBlock(path, opt) ? "text-red-600 bg-red-500/10" : "text-muted-foreground/40 hover:text-red-500"}`}>
            <Flag className="h-3.5 w-3.5" strokeWidth={2} fill={isBlock(path, opt) ? "currentColor" : "none"} />
          </button>
        )}
      </div>
    );
  };

  return (
    <div className="max-w-5xl space-y-6">
      {!selfToken && lead.pre_fattibilita_blocked && (lead.pre_fattibilita_blocking || []).length > 0 && (
        <Card className="rounded border-amber-500/50 bg-amber-500/10" data-testid="lead-blocking-banner">
          <div className="p-5">
            <div className="flex items-center gap-2 text-amber-500 font-semibold"><AlertTriangle className="h-5 w-5" /> Richiede la valutazione del team tecnico</div>
            <ul className="mt-2 space-y-1 text-sm text-foreground/90">
              {(lead.pre_fattibilita_blocking || []).map((b, i) => (
                <li key={i} className="flex gap-2" data-testid={`lead-blocking-item-${i}`}><span className="text-amber-500">•</span><span><b>{b.answer}</b> — {b.reason}</span></li>
              ))}
            </ul>
            <p className="mt-2 text-[11px] text-muted-foreground">Segnalazione automatica dall'intervista del configuratore pubblico.</p>
          </div>
        </Card>
      )}
      {!selfToken && lead.intervista_web && <IntervistaWebCard data={lead.intervista_web} />}
      {!selfToken && (
        <Card className="rounded border-border" data-testid="pf-photos-card">
          <div className="p-5">
            <h2 className="font-heading font-semibold flex items-center gap-2 mb-1"><ImageIcon className="h-5 w-5 text-primary" strokeWidth={1.5} /> Allegati fotografici</h2>
            <p className="text-xs text-muted-foreground mb-4">Foto del sopralluogo (una per tipo). Vengono stampate nella scheda PAD.</p>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              {PHOTO_SLOTS.map((s) => (
                <PhotoSlot key={s.key} leadId={lead.id} slot={s.key} label={s.label} photo={(lead.pre_fattibilita_photos || {})[s.key]} onChanged={onUpdate} />
              ))}
            </div>
          </div>
        </Card>
      )}
      <div className="grid lg:grid-cols-[1fr_360px] gap-6 items-start" data-testid="pre-fattibilita-panel">
      {/* Colonna intervista */}
      <Card className="rounded border-border">
        <div className="p-5 border-b border-border">
          <h2 className="font-heading font-semibold flex items-center gap-2"><ClipboardCheck className="h-5 w-5 text-primary" strokeWidth={1.5} /> Pre-fattibilità tecnica</h2>
          <p className="text-xs text-muted-foreground mt-1">Intervista tecnica al cliente. Segna con <Flag className="h-3 w-3 inline text-red-500" fill="currentColor" /> le condizioni bloccanti; le considerazioni si generano in automatico.</p>
        </div>
        <div className="p-2">
          <Accordion type="multiple" defaultValue={["copertura", "esito"]} className="w-full">
            {SECTIONS.map((sec) => {
              const secHasBlock = activeBlock.some((k) => k.startsWith(`${sec.key}.`));
              return (
                <AccordionItem key={sec.key} value={sec.key} className="border-border">
                  <AccordionTrigger className="px-3 py-3 text-sm hover:no-underline" data-testid={`pf-section-${sec.key}`}>
                    <span className="flex items-center gap-2">{sec.title}{secHasBlock && <span className="h-1.5 w-1.5 rounded-full bg-red-500" />}</span>
                  </AccordionTrigger>
                  <AccordionContent className="px-3 pb-4">
                    <div className="grid sm:grid-cols-2 gap-x-6 gap-y-3">
                      {sec.groups.map((g) => {
                        const path = `${sec.key}.${g.key}`;
                        return (
                          <div key={path}>
                            {g.label && <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold mb-1">{g.label}</div>}
                            {g.options.map((opt) => <OptionRow key={opt} path={path} opt={opt} />)}
                            {g.altro && (
                              <>
                                <OptionRow path={path} opt="Altro" />
                                {isSel(path, "Altro") && <Input value={altro[path] || ""} onChange={(e) => setAltro((a) => ({ ...a, [path]: e.target.value }))} placeholder="Specificare…" className="mt-1 h-8 text-sm" data-testid={`pf-altro-${path}`} />}
                              </>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </AccordionContent>
                </AccordionItem>
              );
            })}
          </Accordion>
        </div>
      </Card>

      {/* Colonna esito + considerazioni automatiche */}
      <Card className="rounded border-border lg:sticky lg:top-20" data-testid="pf-esito-col">
        <div className="p-5 space-y-4">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold mb-2">Esito</div>
            <div className="flex items-center gap-2 flex-wrap">
              {esito ? <span className={`text-xs font-semibold px-2.5 py-1 rounded-full border ${INSTALL_TONE[esito] || "bg-secondary text-foreground border-border"}`} data-testid="pf-esito-badge">{esito}</span> : <span className="text-xs text-muted-foreground">Non definito</span>}
              {activeBlock.length > 0 && <span className="text-xs font-semibold px-2.5 py-1 rounded-full bg-red-500/15 text-red-600 border border-red-500/30 flex items-center gap-1" data-testid="pf-block-count"><AlertTriangle className="h-3 w-3" /> {activeBlock.length} bloccant{activeBlock.length === 1 ? "e" : "i"}</span>}
            </div>
          </div>

          <div>
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold mb-2 flex items-center gap-1.5"><Sparkles className="h-3.5 w-3.5 text-primary" /> Considerazioni automatiche</div>
            <div className="rounded border border-border bg-secondary/40 p-3 max-h-[420px] overflow-y-auto space-y-2" data-testid="pf-considerazioni">
              {activeBlock.length > 0 && (
                <div className="text-xs bg-red-500/10 border border-red-500/20 rounded p-2 text-red-700">
                  <span className="font-semibold flex items-center gap-1 mb-0.5"><AlertTriangle className="h-3.5 w-3.5" /> Condizioni bloccanti</span>
                  {blockLabels.join(" · ")}
                </div>
              )}
              {recap.length === 0 ? (
                <p className="text-xs text-muted-foreground italic">Seleziona le voci dell'intervista: le considerazioni tecniche compariranno qui automaticamente.</p>
              ) : (
                recap.map((r) => (
                  <div key={r.title} className="text-xs">
                    <span className="font-semibold text-foreground">{r.title}:</span> <span className="text-muted-foreground">{r.text}</span>
                  </div>
                ))
              )}
            </div>
          </div>

          <Button onClick={save} disabled={saving} className="w-full font-semibold" data-testid="pf-save-btn">
            {saving ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Save className="h-4 w-4 mr-1.5" />} Salva pre-fattibilità
          </Button>
          {pf.updated_by && <p className="text-[10px] text-muted-foreground text-center">Ultimo aggiornamento: {pf.updated_by}</p>}
        </div>
      </Card>
      </div>
    </div>
  );
}
